'use client';

/**
 * BreadcrumbLeafContext — Permite que una página sobrescriba la etiqueta del ÚLTIMO
 * eslabón del breadcrumb del topbar (ej. el nombre real de la plantilla en
 * /admin/correos/[id], en vez del id crudo). El resto del breadcrumb sigue siendo route-based.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface BreadcrumbLeafValue {
	leaf: string | null;
	setLeaf: (label: string | null) => void;
}

const BreadcrumbLeafContext = createContext<BreadcrumbLeafValue | null>(null);

export function BreadcrumbLeafProvider({ children }: { children: ReactNode }) {
	const [leaf, setLeaf] = useState<string | null>(null);
	return (
		<BreadcrumbLeafContext.Provider value={{ leaf, setLeaf }}>
			{children}
		</BreadcrumbLeafContext.Provider>
	);
}

/** Lee la etiqueta override actual (la usa el topbar). */
export function useBreadcrumbLeaf(): string | null {
	return useContext(BreadcrumbLeafContext)?.leaf ?? null;
}

/** Setea la etiqueta del último eslabón mientras la página esté montada; la limpia al salir. */
export function useSetBreadcrumbLeaf(label: string | null | undefined): void {
	const ctx = useContext(BreadcrumbLeafContext);
	useEffect(() => {
		if (!ctx) return;
		ctx.setLeaf(label ?? null);
		return () => ctx.setLeaf(null);
	}, [ctx, label]);
}
